import React, { useState, useEffect, useMemo } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  DashboardLayout,
  Breadcrumb,
  getSidebarBrandLogoNodes,
} from "@ippon/shared-layouts";
import type {
  SidebarMenuItem,
  RouteEntry as BreadcrumbRouteEntry,
} from "@ippon/shared-layouts";
import {
  Box,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
} from "@mui/material";
import { OpenInNew } from "@mui/icons-material";
import {
  profileImg,
  logosPng,
  ipponTradingLogoSpXs,
} from "@ippon/shared-assets";
import {
  getFullName,
  getEmployeeID,
  getServiceIds,
  getClientID,
  getSecureCookie,
  setSecureCookie,
} from "../../utils/authUtils";
import ipponApi from "../../config/axios";

import { useSelector, useDispatch } from "react-redux";
import { logoutAPI } from "../../store/authSlice";
import { fetchDetailedClient } from "@/store/clientSlice";
import { useAiUsageAccess } from "@/hooks/useAiUsageAccess";
import { getDefaultSettingPath } from "@/route/defaultSettingPath";

// サイドバーメニュー設定
// 利用状況(ai-usage)は owner かつクライアントが「物件管理」を利用中のときのみ表示する
// 「利用状況」「請求」は設定とは異なるため、設定メニューとは独立したトップレベル項目にする。
// subItems が1件のメニューは Sidebar 側で直接リンク（クリックで即遷移）として扱われる。
const buildSidebarMenus = (showAiUsage: boolean): SidebarMenuItem[] => [
  // 利用状況（設定とは別・owner かつ物件管理利用中のみ）
  ...(showAiUsage
    ? [
        {
          id: "ai-usage",
          label: "利用状況",
          icon: (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5 9.2h3V19H5V9.2zM10.6 5h2.8v14h-2.8V5zM16.2 13H19v6h-2.8v-6z"
                fill="currentColor"
              />
            </svg>
          ),
          subItems: [{ to: "/setting/ai-usage", label: "利用状況" }],
        },
      ]
    : []),
  // 請求（設定とは別・owner かつ物件管理利用中のみ）
  ...(showAiUsage
    ? [
        {
          id: "billing",
          label: "請求",
          icon: (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6 2c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V8l-6-6H6zm7 7V3.5L18.5 9H13zM8 13h8v1.5H8V13zm0 3h8v1.5H8V16z"
                fill="currentColor"
              />
            </svg>
          ),
          subItems: [{ to: "/setting/billing", label: "請求" }],
        },
      ]
    : []),
  // 設定
  {
    id: "setting",
    label: "設定",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    subItems: [
      { to: "/setting/account", label: "アカウント設定" },
      { to: "/setting/company", label: "会社情報" },
      { to: "/setting/email-domain", label: "メールドメイン設定" },
    ],
  },
];

// 設定メニュー（下部に固定）
// 「プロフィール」「アカウント管理」は実際に存在するルート(/setting/account 配下)を指す。
// 存在しないルート(/settings/* )を指していると react-router の catch-all(*)に一致して
// /404 へ強制的に遷移してしまうため、実ルートに修正している。
// 「システム設定」に対応する画面が現状無いため項目自体を削除。
const buildBottomMenus = (ownEmployeeId: string): SidebarMenuItem[] => [
  {
    id: "settings",
    label: "設定",
    icon: (
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    subItems: [
      { to: `/setting/account/${ownEmployeeId}`, label: "プロフィール" },
      { to: "/setting/account", label: "アカウント管理" },
    ],
  },
];

// パンくずリスト設定

const routeConfig: Record<string, BreadcrumbRouteEntry> = {
  "/": {
    breadcrumb: [],
  },
  "/setting/account": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: [
      { path: "/setting/account", title: "管理" },
      { path: "/setting/account", title: "アカウント設定" },
    ],
  },
  "/setting/account/:employee_id": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: ({ params, employeeName }: any) => [
      { path: "/setting/account", title: "管理" },
      { path: "/setting/account", title: "アカウント設定" },
      {
        path: `/setting/account/${params?.employee_id}`,
        title: employeeName ?? "",
      },
    ],
  },
  "/setting/company": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: [
      { path: "/setting/company", title: "管理" },
      { path: "/setting/company", title: "会社情報" },
    ],
  },
  "/setting/email-domain": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: [
      { path: "/setting/email-domain", title: "管理" },
      { path: "/setting/email-domain", title: "メールドメイン設定" },
    ],
  },
  "/setting/ai-usage": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: [{ path: "/setting/ai-usage", title: "利用状況" }],
  },
  "/setting/billing": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: [{ path: "/setting/billing", title: "請求" }],
  },
  "/setting/billing/:id": {
    icon: (
      <svg
        width="15"
        height="15"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M12 15.5A3.5 3.5 0 0 1 8.5 12A3.5 3.5 0 0 1 12 8.5A3.5 3.5 0 0 1 15.5 12A3.5 3.5 0 0 1 12 15.5ZM19.43 12.97C19.47 12.65 19.5 12.33 19.5 12C19.5 11.67 19.47 11.34 19.43 11L21.54 9.37C21.73 9.22 21.78 8.95 21.66 8.73L19.66 5.27C19.54 5.05 19.27 4.96 19.05 5.05L16.56 6.05C16.04 5.66 15.5 5.32 14.87 5.07L14.5 2.42C14.46 2.18 14.25 2 14 2H10C9.75 2 9.54 2.18 9.5 2.42L9.13 5.07C8.5 5.32 7.96 5.66 7.44 6.05L4.95 5.05C4.73 4.96 4.46 5.05 4.34 5.27L2.34 8.73C2.22 8.95 2.27 9.22 2.46 9.37L4.57 11C4.53 11.34 4.5 11.67 4.5 12C4.5 12.33 4.53 12.65 4.57 12.97L2.46 14.63C2.27 14.78 2.22 15.05 2.34 15.27L4.34 18.73C4.46 18.95 4.73 19.03 4.95 18.95L7.44 17.94C7.96 18.34 8.5 18.68 9.13 18.93L9.5 21.58C9.54 21.82 9.75 22 10 22H14C14.25 22 14.46 21.82 14.5 21.58L14.87 18.93C15.5 18.68 16.04 18.34 16.56 17.94L19.05 18.95C19.27 19.03 19.54 18.95 19.66 18.73L21.66 15.27C21.78 15.05 21.73 14.78 21.54 14.63L19.43 12.97Z"
          fill="currentColor"
        />
      </svg>
    ),
    breadcrumb: [
      { path: "/setting/billing", title: "請求" },
      { path: "", title: "請求詳細" },
    ],
  },
};

interface ServiceItem {
  id: string;
  name: string;
  base_url: string;
}

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const dispatch = useDispatch();

  // ロゴメニュー用のstate
  const [logoMenuAnchor, setLogoMenuAnchor] = useState<null | HTMLElement>(
    null
  );
  const [services, setServices] = useState<ServiceItem[]>([]);

  const { canAccess: canAccessAiUsage, loading: aiUsageAccessLoading } =
    useAiUsageAccess();
  const sidebarMenus = useMemo(
    () => buildSidebarMenus(canAccessAiUsage),
    [canAccessAiUsage]
  );

  useEffect(() => {
    const clientId = getClientID();
    if (clientId) {
      void dispatch(fetchDetailedClient(clientId) as any);
    }
  }, [dispatch]);

  useEffect(() => {
    if (location.pathname !== "/" || aiUsageAccessLoading) return;
    navigate(getDefaultSettingPath(canAccessAiUsage), { replace: true });
  }, [
    aiUsageAccessLoading,
    canAccessAiUsage,
    location.pathname,
    navigate,
  ]);
  const bottomMenus = useMemo(
    () => buildBottomMenus(getEmployeeID() || ""),
    []
  );

  // 従業員データを取得
  const { data: employeeData } = useSelector(
    (state: any) => state.employees.detailed
  );

  // 従業員名を生成
  const employeeName =
    employeeData?.first_name && employeeData?.last_name
      ? `${employeeData.last_name} ${employeeData.first_name}`
      : undefined;

  // サービス情報を取得
  useEffect(() => {
    const fetchServices = async () => {
      try {
        // クッキーから取得を試みる
        const cachedServices = getSecureCookie("my_use_service");
        if (cachedServices) {
          try {
            const parsed = JSON.parse(cachedServices);
            if (Array.isArray(parsed) && parsed.length > 0) {
              setServices(parsed);
              return;
            }
          } catch (error) {
            console.error("Error parsing cached services:", error);
          }
        }

        // Cognitoからservice_idsを取得
        const serviceIds = getServiceIds();
        if (!serviceIds || serviceIds.length === 0) {
          setServices([]);
          return;
        }

        const clientId = getClientID();
        if (!clientId) {
          console.error("Client ID not found");
          return;
        }

        // APIを呼び出してサービス情報を取得
        const response = await ipponApi.get("/api/v1/services", {
          params: {
            client_id: clientId,
            service_ids: JSON.stringify(serviceIds),
          },
        });

        if (response.data && response.data.success && response.data.data) {
          const serviceData = response.data.data;
          setServices(serviceData);

          // クッキーに保存（7日間有効）
          setSecureCookie("my_use_service", JSON.stringify(serviceData), 7);
        }
      } catch (error) {
        console.error("Error fetching services:", error);
        setServices([]);
      }
    };

    fetchServices().then(() => {
      setServices((prev) => {
        if (prev.length > 0 || !import.meta.env.DEV) return prev;
        return [
          {
            id: "dev-property",
            name: "物件管理",
            base_url: "https://property-management.ippon-cloud.com",
          },
          {
            id: "dev-sales",
            name: "売買仲介",
            base_url: "https://sales-brokerage.ippon-cloud.com",
          },
        ];
      });
    });
  }, []);

  const brandLogo = getSidebarBrandLogoNodes({
    label: "設定管理",
    logoSrc: logosPng,
    logoCollapsedSrc: ipponTradingLogoSpXs,
  });

  const handleLogoClick = (event: React.MouseEvent<HTMLElement>) => {
    // サービスが1件もない場合は何もしない
    if (services.length === 0) {
      return;
    }
    setLogoMenuAnchor(event.currentTarget);
  };

  const handleLogoMenuClose = () => {
    setLogoMenuAnchor(null);
  };

  const handleExternalLink = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
    handleLogoMenuClose();
  };

  const handleLogout = async () => {
    try {
      // ログアウトAPIを呼び出し
      // logoutAPI.fulfilled または logoutAPI.rejected でクッキーとストレージがクリアされる
      await dispatch(logoutAPI() as any);
      // ログイン画面に遷移
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
      // エラーが発生してもログイン画面に遷移
      navigate("/login");
    }
  };

  return (
    <DashboardLayout
      showMobileHeader={true}
      logo={null}
      headerProps={
        {
          fullName: getFullName() || "",
          employeeId: getEmployeeID() || "",
          onLogout: handleLogout,
          showUserInfo: true,
          profileImage: profileImg,
          profilePath: "/setting/account",
          breadcrumb: !isMobile ? (
            <Breadcrumb routeConfig={routeConfig} employeeName={employeeName} />
          ) : null,
          defaultMenuItems: true,
          showUserMenu: true,
          onNavigate: (path: string) => navigate(path),
        } as any
      }
      sidebarProps={{
        menus: sidebarMenus,
        bottomMenus: bottomMenus,
        currentPath: location.pathname,
        onNavigate: (path: string) => {
          console.log("Navigating to:", path);
          navigate(path);
        },
        onMenuItemClick: (menuId: string, subItemTo?: string) => {
          console.log("Menu item clicked:", menuId, "subItem:", subItemTo);
        },
        logo: (
          <>
            <Box
              onClick={handleLogoClick}
              sx={{
                cursor: services.length > 0 ? "pointer" : "default",
                "&:hover": {
                  opacity: services.length > 0 ? 0.8 : 1,
                },
              }}
            >
              {brandLogo.logo}
            </Box>
            <Menu
              anchorEl={logoMenuAnchor}
              open={Boolean(logoMenuAnchor)}
              onClose={handleLogoMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              {services.map((service) => (
                <MenuItem
                  key={service.id}
                  onClick={() => handleExternalLink(service.base_url)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: "14px",
                    }}
                  >
                    {service.name}
                    <OpenInNew sx={{ fontSize: 16 }} />
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </>
        ),
        logoCollapsed: (
          <>
            <Box
              onClick={handleLogoClick}
              sx={{
                cursor: services.length > 0 ? "pointer" : "default",
                "&:hover": {
                  opacity: services.length > 0 ? 0.8 : 1,
                },
              }}
            >
              {brandLogo.logoCollapsed}
            </Box>
            <Menu
              anchorEl={logoMenuAnchor}
              open={Boolean(logoMenuAnchor)}
              onClose={handleLogoMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              {services.map((service) => (
                <MenuItem
                  key={service.id}
                  onClick={() => handleExternalLink(service.base_url)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      fontSize: "14px",
                    }}
                  >
                    {service.name}
                    <OpenInNew sx={{ fontSize: 16 }} />
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </>
        ),
        theme: {
          primaryColor: "#0E9DBC",
          hoverColor: "#DBF8FF",
          activeColor: "#0e9dbc",
        },
      }}
    >
      <Outlet />
    </DashboardLayout>
  );
};

export default MainLayout;
